import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';

import { taskColumns } from '@utils/columns';

import withPagination from '@shared/withFilteredPagination';
import FilterTable from '@shared/FilterTable';
import TaskResourceList from './components/TaskResourceList';
import ArchiveTaskAction from './components/ArchiveTaskAction';

import { Box, Button, ToggleButton, ToggleButtonGroup } from '@mui/material';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import ViewListIcon from '@mui/icons-material/ViewList';
import { AppSelect, AppToast, MOBILE_BREAKPOINT } from '@shared';

const propTypes = {
  records: PropTypes.array.isRequired,
  newUrl: PropTypes.string,
  logisticsTeamId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showArchivedFilter: PropTypes.bool,
}

const defaultProps = {

}

const TaskIndex = (props) => {
  const [view, setView] = useState("table");
  const [toast, setToast] = useState(null);

  // Operations loses track of unassigned work in the day to day, so this opens the one list they
  // check: our own logistics team, nobody assigned. The team comes from a setting, not a fixed id.
  const unassignedURL = () => {
    const params = new URLSearchParams({ status: 'unassigned' });
    if (props.logisticsTeamId) params.set('team_id', props.logisticsTeamId);

    return `/tasks?${params.toString()}`;
  };

  const primaryAction = props.newUrl ? { content: I18n.t("actions.create_x", { x: I18n.t("activerecord.models.task.one") }), url: props.newUrl } : null;
  
  const hideToast = () => {
    setToast(null);
  }
 
  // If view isn't list then don't bother processing the component
  const resourceListView = () => {
    if(view == "list")
      return ( 
      <TaskResourceList 
          loading={ props.loading } 
          items={ props.records }  
          itemProps={{
            extraActions: (taskId, task) => (
              <div className="flex mt4">
                  { task.archive_url &&
                    <div className="mr8">
                      <ArchiveTaskAction archiveURL={ task.archive_url } />
                    </div>
                  }
                  <div>
                    <Button
                        variant="contained"
                        href={`/tasks/${taskId}`}>
                      {I18n.t("actions.view_details")}
                    </Button>
                  </div>
              </div>
            )
          }}
        />
    )
  }

  const TaskListMemo = useCallback(resourceListView, [props.loading, props.records, view]);
  return(
    <>
      <FilterTable
        searchName="search_query"
        searchPlaceholder={"ID de Orden"}
        pageProps={{
          title: I18n.t("activerecord.models.task.other"),
          primaryAction: primaryAction,
        }}
        columnsInfo={ taskColumns }
        resourceTableProps={{
          itemURLAsAction: false,
          mobileCellLabels: true,
        }}
        contentComponent={ view == "list" ? TaskListMemo : null }
        resourceTableClassName={ "resource-table-tasks" }
        filterFooter={
          <div className="mt20" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button variant="outlined" color="inherit" href={ unassignedURL() }>Sin asignar de Logística</Button>

            {/* Paired so the two share one row on a phone. Left loose they are wider
                than Polaris' were, so only one fit and the other dropped a line. */}
            <Box sx={{
              display: 'flex', gap: 1.5, alignItems: 'center',
              flex: '1 1 100%',
              '& .MuiFormControl-root': { flex: 1, minWidth: 0 },
              [`@media (min-width:${MOBILE_BREAKPOINT + 1}px)`]: {
                flex: '0 0 auto',
                '& .MuiFormControl-root': { flex: '0 0 auto', minWidth: 160 },
              },
            }}>
              <AppSelect
                size="small"
                fullWidth={ false }
                label="Traslados"
                disabled={ props.loading }
                options={[{ label: 'Todos', value: ' ' }, { label: 'A proveedor/taller', value: 'true' }]}
                onChange={ (value) => props.handleFilterChange(value, 'supplier_leg') }
                value={ props.filterState?.supplier_leg || ' ' }
              />

              {/* Archived tasks are out of the daily list; this is the only way to see them. */}
              { props.showArchivedFilter &&
              <AppSelect
                size="small"
                fullWidth={ false }
                label="Tareas"
                disabled={ props.loading }
                options={[{ label: 'Activas', value: ' ' }, { label: 'Archivadas', value: 'true' }]}
                onChange={ (value) => props.handleFilterChange(value, 'archived') }
                value={ props.filterState?.archived || ' ' }
              />
              }
            </Box>

            {/* Polaris' segmented ButtonGroup; MUI states the either/or directly. */}
            <ToggleButtonGroup
              exclusive
              size="small"
              value={ view }
              onChange={ (_event, newView) => { if (newView) setView(newView); } }
            >
              <ToggleButton value="table" aria-label="Vista de tabla">
                <Inventory2OutlinedIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="list" aria-label="Vista de lista">
                <ViewListIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
          </div>
        }
        {...props}
      />

      { toast && <AppToast content={ toast } onDismiss={hideToast} /> }
    </>
  )
};

TaskIndex.propTypes = propTypes;
TaskIndex.defaultProps = defaultProps;

// A blank filter is " ", not "", so a value coming from the URL has to replace the whole default.
// Reading them here is what makes a filtered view shareable: the link reopens the same list.
const filtersFromURL = () => {
  const params = new URLSearchParams(window.location.search);
  const defaults = { search_query: "", kind: " ", status: " ", team_id: " ", courier_id: " ", archived: " ", supplier_leg: " " };

  return Object.keys(defaults).reduce((filters, key) => {
    const value = params.get(key);

    return value ? { ...filters, [key]: value } : filters;
  }, defaults);
};

export default withPagination(TaskIndex, {
  mapPropsToInitialFilters: filtersFromURL,
  mapPropsToInitialRecords: (props) => props.records,
  mapPropsToPagy: (props) => props.pagy,
  paramsName: "task_filters",
  filterURL: "/tasks/filter",
});